import React, {useEffect, useMemo, useRef, useState} from "react";
import {trackClick, trackEvent, trackFormSubmit} from "@lib/gtm";
import {
    APP_CONSENT_STORAGE_KEY,
    APP_PLATFORM_STORAGE_KEY,
    APP_SITE_CONTEXT_STORAGE_KEY,
    getStoredAppConsent,
    getStoredAppPlatform,
    getStoredAppSiteContext
} from "@lib/AppConsent/appConsent";

type DlEntry = Record<string, any>;

type DebugSnapshot = {
    href: string;
    userAgent: string;
    queryParams: Record<string, string>;
    appTrackingConsentGlobal: string | null;
    siteContextGlobal: string | null;
    appPlatformGlobal: string | null;
    gtmEnabledGlobal: boolean | null;
    storedTrackingConsent: string | null;
    storedSiteContext: string | null;
    storedPlatform: string | null;
    dataLayerLength: number;
    cookies: Array<{ name: string; value: string }>;
    trackingCookies: Array<{ name: string; value: string }>;
    setupEnvironment: string | null;
    setupDeviceType: string | null;
    attPolicyType: string | null;
    attPolicyLabel: boolean | null;
    cookieConsentRaw: string | null;
    cookieConsentParsed: {
        raw: string;
        necessary: boolean | null;
        preferences: boolean | null;
        statistics: boolean | null;
        marketing: boolean | null;
        method: string | null;
        region: string | null;
        ver: number | null;
        utc: number | null;
        stamp: string | null;
    } | null;
};

function readQueryParams(): Record<string, string> {
    const out: Record<string, string> = {};
    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
        out[key] = value;
    });

    return out;
}

function cloneDataLayer(): DlEntry[] {
    return Array.isArray(globalThis.dataLayer) ? [...globalThis.dataLayer] : [];
}

function readCookies() {
    if (!document.cookie) return [];

    return document.cookie
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
            const eqIndex = part.indexOf("=");

            if (eqIndex < 0) {
                return {name: part, value: ""};
            }

            return {
                name: part.slice(0, eqIndex),
                value: part.slice(eqIndex + 1),
            };
        });
}

function getCookieValue(name: string): string | null {
    const cookie = readCookies().find((item) => item.name === name);
    return cookie?.value ?? null;
}

function parseCookieConsentValue(raw: string | null) {
    if (!raw) return null;

    try {
        const decoded = decodeURIComponent(raw);

        const readBool = (key: string) => {
            const match = decoded.match(new RegExp(`${key}:(true|false)`));
            return match ? match[1] === "true" : null;
        };

        const readString = (key: string) => {
            const match = decoded.match(new RegExp(`${key}:%27([^%]+?)%27|${key}:'([^']+)'`));
            return match ? (match[1] || match[2] || null) : null;
        };

        const readNumber = (key: string) => {
            const match = decoded.match(new RegExp(`${key}:(\\d+)`));
            return match ? Number(match[1]) : null;
        };

        return {
            raw: decoded,
            necessary: readBool("necessary"),
            preferences: readBool("preferences"),
            statistics: readBool("statistics"),
            marketing: readBool("marketing"),
            method: readString("method"),
            region: readString("region"),
            ver: readNumber("ver"),
            utc: readNumber("utc"),
            stamp: readString("stamp"),
        };
    } catch {
        return {
            raw,
            necessary: null,
            preferences: null,
            statistics: null,
            marketing: null,
            method: null,
            region: null,
            ver: null,
            utc: null,
            stamp: null,
        };
    }
}

function isTrackingCookieName(name: string) {
    return (
        name === "_ga" ||
        name.startsWith("_ga_") ||
        name === "_gid" ||
        name === "_gat" ||
        name === "_fbp" ||
        name.startsWith("_gcl_") ||
        name === "_clck" ||
        name === "_clsk"
    );
}

function getLatestEvent(eventName: string): DlEntry | null {
    const items = cloneDataLayer();

    for (let i = items.length - 1; i >= 0; i--) {
        if (items[i]?.event === eventName) {
            return items[i];
        }
    }

    return null;
}

function buildSnapshot(): DebugSnapshot {
    const cookies = readCookies();
    const trackingCookies = cookies.filter((cookie) => isTrackingCookieName(cookie.name));

    const latestSetup = getLatestEvent("wSetup");
    const latestAttPolicy = getLatestEvent("GA4event");

    const cookieConsentRaw = getCookieValue("CookieConsent");
    const cookieConsentParsed = parseCookieConsentValue(cookieConsentRaw);

    return {
        href: window.location.href,
        userAgent: navigator.userAgent,
        queryParams: readQueryParams(),
        appTrackingConsentGlobal: globalThis.__APP_TRACKING_CONSENT__ ?? null,
        siteContextGlobal: globalThis.__SITE_CONTEXT__ ?? null,
        storedTrackingConsent: getStoredAppConsent(),
        storedSiteContext: getStoredAppSiteContext(),
        dataLayerLength: cloneDataLayer().length,
        appPlatformGlobal: globalThis.__APP_PLATFORM__ ?? null,
        gtmEnabledGlobal: typeof globalThis.__GTM_ENABLED__ === "boolean" ? globalThis.__GTM_ENABLED__ : null,
        storedPlatform: getStoredAppPlatform(),
        cookies,
        trackingCookies,
        setupEnvironment: latestSetup?.environment ?? null,
        setupDeviceType: latestSetup?.deviceType ?? null,
        attPolicyType:
            latestAttPolicy?.eventname === "att_policy" && latestAttPolicy?.action === "consent"
                ? (latestAttPolicy.type ?? null)
                : null,
        attPolicyLabel:
            latestAttPolicy?.eventname === "att_policy" && latestAttPolicy?.action === "consent"
                ? (typeof latestAttPolicy.label === "boolean" ? latestAttPolicy.label : null)
                : null,
        cookieConsentRaw,
        cookieConsentParsed,

    };
}

function safeJsonStringify(value: unknown, space = 2): string {
    const seen = new WeakSet();

    return JSON.stringify(
        value,
        (_key, currentValue) => {
            if (typeof currentValue === "function") {
                return `[Function ${currentValue.name || "anonymous"}]`;
            }

            if (typeof window !== "undefined") {
                if (currentValue === window) return "[Window]";
                if (currentValue === document) return "[Document]";

                if (currentValue instanceof Node) {
                    return `[DOM ${currentValue.nodeName}]`;
                }

                if (currentValue instanceof Event) {
                    return {
                        type: currentValue.type,
                        target: currentValue.target instanceof Node
                            ? `[DOM ${currentValue.target.nodeName}]`
                            : "[EventTarget]",
                        currentTarget: currentValue.currentTarget instanceof Node
                            ? `[DOM ${currentValue.currentTarget.nodeName}]`
                            : "[EventTarget]",
                    };
                }
            }

            if (typeof currentValue === "object" && currentValue !== null) {
                if (seen.has(currentValue)) return "[Circular]";
                seen.add(currentValue);
            }

            return currentValue;
        },
        space
    );
}

function summarizeEntry(entry: DlEntry) {
    const eventName = typeof entry.event === "string" ? entry.event : "(no event)";
    const keys = Object.keys(entry);

    const importantKeys = keys.filter(
        (key) =>
            key !== "event" &&
            key !== "gtm.uniqueEventId" &&
            key !== "gtm.start"
    );

    return {
        eventName,
        uniqueEventId: entry["gtm.uniqueEventId"],
        gtmStart: entry["gtm.start"],
        importantKeys,
    };
}

function getEventTone(eventName: string) {
    if (eventName.startsWith("gtm.")) return "#6b7280";
    if (eventName === "wSetup") return "#0f766e";
    if (eventName === "GA4event") return "#1d4ed8";
    if (eventName === "virtual_pageview") return "#7c3aed";
    if (eventName === "cta_click" || eventName === "form_submit") return "#b45309";
    return "#111827";
}

function prettyValue(value: any): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return safeJsonStringify(value, 2);
}

function SnapshotCard({label, value}: { label: string; value: React.ReactNode }) {
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 14,
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
        >
            <div style={{fontSize: 12, color: "#6b7280", marginBottom: 6}}>{label}</div>
            <div style={{fontSize: 14, color: "#111827", fontWeight: 600, wordBreak: "break-word"}}>
                {value}
            </div>
        </div>
    );
}

function EventCard({entry, index}: { entry: DlEntry; index: number }) {
    const summary = summarizeEntry(entry);
    const tone = getEventTone(summary.eventName);

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                    marginBottom: 12,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div style={{fontSize: 12, color: "#6b7280", marginBottom: 4}}>
                        #{index + 1}
                    </div>
                    <div
                        style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: tone,
                            wordBreak: "break-word",
                        }}
                    >
                        {summary.eventName}
                    </div>
                </div>

                <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                    {summary.uniqueEventId !== undefined && (
                        <span
                            style={{
                                fontSize: 12,
                                background: "#f3f4f6",
                                color: "#374151",
                                borderRadius: 999,
                                padding: "6px 10px",
                            }}
                        >
                            gtm.uniqueEventId: {String(summary.uniqueEventId)}
                        </span>
                    )}
                    {summary.gtmStart !== undefined && (
                        <span
                            style={{
                                fontSize: 12,
                                background: "#f3f4f6",
                                color: "#374151",
                                borderRadius: 999,
                                padding: "6px 10px",
                            }}
                        >
                            gtm.start: {String(summary.gtmStart)}
                        </span>
                    )}
                </div>
            </div>

            {summary.importantKeys.length > 0 && (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 10,
                        marginBottom: 14,
                    }}
                >
                    {summary.importantKeys.map((key) => (
                        <div
                            key={key}
                            style={{
                                background: "#f9fafb",
                                border: "1px solid #f3f4f6",
                                borderRadius: 12,
                                padding: 10,
                            }}
                        >
                            <div style={{fontSize: 11, color: "#6b7280", marginBottom: 4}}>
                                {key}
                            </div>
                            <div
                                style={{
                                    fontSize: 13,
                                    color: "#111827",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {prettyValue(entry[key])}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <details>
                <summary
                    style={{
                        cursor: "pointer",
                        color: "#2563eb",
                        fontSize: 13,
                        fontWeight: 600,
                    }}
                >
                    Payload completo
                </summary>
                <pre
                    style={{
                        marginTop: 10,
                        padding: 12,
                        background: "#0b1020",
                        color: "#d1d5db",
                        borderRadius: 12,
                        overflowX: "auto",
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {safeJsonStringify(entry, 2)}
                </pre>
            </details>
        </div>
    );
}

export default function GtmTestPage() {
    const originalPushRef = useRef<typeof Array.prototype.push | null>(null);
    const [snapshot, setSnapshot] = useState<DebugSnapshot>(() => buildSnapshot());
    const [dataLayerItems, setDataLayerItems] = useState<DlEntry[]>(() => cloneDataLayer());
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const prettyQueryParams = useMemo(
        () => safeJsonStringify(snapshot.queryParams, 2),
        [snapshot.queryParams]
    );

    useEffect(() => {
        if (!globalThis.dataLayer) {
            setDataLayerItems([]);
            setSnapshot(buildSnapshot());
            return;
        }

        if (!originalPushRef.current) {
            originalPushRef.current = globalThis.dataLayer.push.bind(globalThis.dataLayer);
        }

        const originalPush = originalPushRef.current;

        globalThis.dataLayer.push = function (...items: any[]) {
            const result = originalPush(...items);
            setDataLayerItems(cloneDataLayer());
            setSnapshot(buildSnapshot());
            return result;
        };

        setDataLayerItems(cloneDataLayer());
        setSnapshot(buildSnapshot());

        const onStorage = () => {
            setSnapshot(buildSnapshot());
            setDataLayerItems(cloneDataLayer());
        };

        window.addEventListener("storage", onStorage);

        return () => {
            if (globalThis.dataLayer && originalPushRef.current) {
                globalThis.dataLayer.push = originalPushRef.current;
            }
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    function refreshAll() {
        setSnapshot(buildSnapshot());
        setDataLayerItems(cloneDataLayer());
    }

    function clearDebugStorage() {
        localStorage.removeItem(APP_CONSENT_STORAGE_KEY);
        localStorage.removeItem(APP_SITE_CONTEXT_STORAGE_KEY);
        localStorage.removeItem(APP_PLATFORM_STORAGE_KEY);
        refreshAll();
    }

    function clearDataLayer() {
        if (!globalThis.dataLayer) return;
        globalThis.dataLayer.length = 0;
        refreshAll();
    }

    function sendTestEvent() {
        trackEvent("debug_custom_event", {
            source: "debug-page",
            ts: Date.now(),
            debug: true,
        });
    }

    function sendTestClick() {
        trackClick({
            cta_name: "debug_cta",
            source: "debug-page",
            ts: Date.now(),
        });
    }

    function sendTestFormSubmit() {
        trackFormSubmit({
            form_name: "debug_form",
            source: "debug-page",
            ts: Date.now(),
        });
    }

    const btnStyle: React.CSSProperties = {
        background: "#2563eb",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: "8px 14px",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
    };

    const btnSecondary: React.CSSProperties = {
        ...btnStyle,
        background: "#374151",
    };

    const btnDanger: React.CSSProperties = {
        ...btnStyle,
        background: "#b91c1c",
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f3f4f6",
                padding: 24,
            }}
        >
            <div style={{maxWidth: 1280, margin: "0 auto"}}>
                <div style={{marginBottom: 24}}>
                    <h1 style={{margin: 0, fontSize: 28, color: "#111827"}}>GTM / Consent Debug</h1>
                    <p style={{marginTop: 8, color: "#6b7280"}}>
                        Stato platform, consenso, contesto, wSetup, GA4event, cookie e dataLayer con log live dei push.
                    </p>
                </div>

                <div style={{display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24}}>
                    <button style={btnSecondary} onClick={refreshAll}>Refresh</button>
                    <button style={btnStyle} onClick={sendTestEvent}>Push test event</button>
                    <button style={btnStyle} onClick={sendTestClick}>Push test click</button>
                    <button style={btnStyle} onClick={sendTestFormSubmit}>Push test form submit</button>
                    <button style={btnDanger} onClick={clearDataLayer}>Clear dataLayer</button>
                    <button style={btnDanger} onClick={clearDebugStorage}>Clear storage</button>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 12,
                        marginBottom: 24,
                    }}
                >
                    <SnapshotCard label="Platform globale (__APP_PLATFORM__)" value={String(snapshot.appPlatformGlobal ?? "null")} />
                    <SnapshotCard label="Platform salvata (localStorage)" value={String(snapshot.storedPlatform ?? "null")} />
                    <SnapshotCard label="GTM enabled (__GTM_ENABLED__)" value={snapshot.gtmEnabledGlobal === null ? "null" : String(snapshot.gtmEnabledGlobal)} />
                    <SnapshotCard label="Site context globale (__SITE_CONTEXT__)" value={String(snapshot.siteContextGlobal ?? "null")} />
                    <SnapshotCard label="Tracking consent globale (__APP_TRACKING_CONSENT__)" value={String(snapshot.appTrackingConsentGlobal ?? "null")} />
                    <SnapshotCard label="Site context salvato (localStorage)" value={String(snapshot.storedSiteContext ?? "null")} />
                    <SnapshotCard label="Tracking consent salvato (localStorage)" value={String(snapshot.storedTrackingConsent ?? "null")} />
                    <SnapshotCard label="Environment" value={String(snapshot.setupEnvironment ?? "-")} />
                    <SnapshotCard label="Device type" value={String(snapshot.setupDeviceType ?? "-")} />
                    <SnapshotCard label="ATT policy type" value={String(snapshot.attPolicyType ?? "-")} />
                    <SnapshotCard label="ATT policy label" value={snapshot.attPolicyLabel === null ? "-" : String(snapshot.attPolicyLabel)} />
                    <SnapshotCard label="Eventi nel dataLayer" value={snapshot.dataLayerLength} />
                    <SnapshotCard label="Cookiebot marketing" value={String(snapshot.cookieConsentParsed?.marketing ?? "-")} />
                    <SnapshotCard label="Cookiebot statistics" value={String(snapshot.cookieConsentParsed?.statistics ?? "-")} />
                    <SnapshotCard label="Tracking cookies" value={snapshot.trackingCookies.length} />
                    <SnapshotCard label="Cookies totali" value={snapshot.cookies.length} />
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "1.2fr 0.8fr",
                        gap: 16,
                        alignItems: "start",
                    }}
                >
                    <div style={{display: "grid", gap: 16}}>
                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 16,
                                padding: 16,
                            }}
                        >
                            <div style={{fontSize: 16, fontWeight: 700, marginBottom: 10}}>CookieConsent</div>

                            {!snapshot.cookieConsentParsed ? (
                                <div style={{fontSize: 13, color: "#6b7280"}}>CookieConsent non presente</div>
                            ) : (
                                <div style={{display: "grid", gap: 10}}>
                                    <div><strong>necessary:</strong> {String(snapshot.cookieConsentParsed.necessary)}</div>
                                    <div><strong>preferences:</strong> {String(snapshot.cookieConsentParsed.preferences)}</div>
                                    <div><strong>statistics:</strong> {String(snapshot.cookieConsentParsed.statistics)}</div>
                                    <div><strong>marketing:</strong> {String(snapshot.cookieConsentParsed.marketing)}</div>
                                    <div><strong>method:</strong> {snapshot.cookieConsentParsed.method ?? "-"}</div>
                                    <div><strong>region:</strong> {snapshot.cookieConsentParsed.region ?? "-"}</div>
                                    <div><strong>ver:</strong> {snapshot.cookieConsentParsed.ver ?? "-"}</div>
                                    <div><strong>utc:</strong> {snapshot.cookieConsentParsed.utc ?? "-"}</div>

                                    <details>
                                        <summary style={{cursor: "pointer", color: "#2563eb", fontWeight: 600}}>
                                            Raw value
                                        </summary>
                                        <pre
                                            style={{
                                                marginTop: 10,
                                                padding: 12,
                                                background: "#f9fafb",
                                                borderRadius: 12,
                                                fontSize: 12,
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {snapshot.cookieConsentParsed.raw}
                                        </pre>
                                    </details>
                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 16,
                                padding: 16,
                            }}
                        >
                            <div style={{fontSize: 16, fontWeight: 700, marginBottom: 10}}>Cookies</div>

                            <div style={{display: "grid", gap: 10}}>
                                {snapshot.cookies.length === 0 ? (
                                    <div style={{fontSize: 13, color: "#6b7280"}}>Nessun cookie visibile da document.cookie</div>
                                ) : (
                                    snapshot.cookies.map((cookie) => {
                                        const isTracking = isTrackingCookieName(cookie.name);

                                        return (
                                            <div
                                                key={cookie.name}
                                                style={{
                                                    background: isTracking ? "#fff7ed" : "#f9fafb",
                                                    border: `1px solid ${isTracking ? "#fdba74" : "#e5e7eb"}`,
                                                    borderRadius: 12,
                                                    padding: 12,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        gap: 8,
                                                        marginBottom: 6,
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <div style={{fontSize: 13, fontWeight: 700, color: "#111827"}}>
                                                        {cookie.name}
                                                    </div>

                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            borderRadius: 999,
                                                            padding: "4px 8px",
                                                            background: isTracking ? "#fed7aa" : "#e5e7eb",
                                                            color: "#374151",
                                                        }}
                                                    >
                                                        {isTracking ? "tracking" : "other/unknown"}
                                                    </span>
                                                </div>

                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#4b5563",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {cookie.value || "-"}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 16,
                                padding: 16,
                            }}
                        >
                            <div style={{fontSize: 16, fontWeight: 700, marginBottom: 10}}>URL corrente</div>
                            <div style={{fontSize: 13, color: "#374151", wordBreak: "break-word"}}>
                                {snapshot.href}
                            </div>
                        </div>

                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 16,
                                padding: 16,
                            }}
                        >
                            <div style={{fontSize: 16, fontWeight: 700, marginBottom: 10}}>Query params</div>
                            <pre
                                style={{
                                    margin: 0,
                                    background: "#f9fafb",
                                    padding: 12,
                                    borderRadius: 12,
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {prettyQueryParams}
                            </pre>
                        </div>

                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: 16,
                                padding: 16,
                            }}
                        >
                            <div style={{fontSize: 16, fontWeight: 700, marginBottom: 10}}>User agent</div>
                            <div style={{fontSize: 12, color: "#374151", wordBreak: "break-word"}}>
                                {snapshot.userAgent}
                            </div>
                        </div>
                    </div>

                    <div>
                        <div style={{marginBottom: 12, fontSize: 18, fontWeight: 700, color: "#111827"}}>
                            Eventi dataLayer
                        </div>

                        <div style={{display: "grid", gap: 14}}>
                            {dataLayerItems.length === 0 ? (
                                <div
                                    style={{
                                        background: "#fff",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 16,
                                        padding: 18,
                                    }}
                                >
                                    Nessun evento nel dataLayer.
                                </div>
                            ) : (
                                dataLayerItems
                                    .filter((entry) => !["gtm.click", "gtm.scrollDepth"].includes(entry.event))
                                    .map((entry, index) => (
                                        <EventCard
                                            key={`${entry.event ?? "event"}-${entry["gtm.uniqueEventId"] ?? index}-${index}`}
                                            entry={entry}
                                            index={index}
                                        />
                                    ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}